import React from 'react';
import PropTypes from 'prop-types';

import { Grid, makeStyles, Paper, Typography } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    maxWidth: 540,
    margin: '6px auto 12px auto',
    paddingLeft: 16,
    paddingRight: 16,
  },
  title: {
    marginBottom: 8,
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
  },
  optionCard: {
    cursor: 'pointer',
    borderRadius: 10,
    padding: '12px 14px',
    border: '1px solid #e0e0e0',
    transition:
      'box-shadow 180ms ease, transform 180ms ease, border-color 180ms ease, background-color 180ms ease',
    userSelect: 'none',
    outline: 'none',
  },
  optionCardDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  optionCardSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
    transform: 'translateY(-1px)',
    backgroundColor: `${theme.palette.primary.main}10`,
  },
  optionHeaderRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 12,
  },
  optionTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  indicatorOuter: {
    width: 14,
    height: 14,
    borderRadius: '50%',
    border: '2px solid #bdbdbd',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
    flexShrink: 0,
  },
  indicatorOuterSelected: {
    borderColor: theme.palette.primary.main,
  },
  indicatorInner: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
  },
  helper: {
    marginTop: 10,
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
    display: 'block',
  },
}));

const AuthTypeSelector = ({ value, options, onChange, disabled }) => {
  const classes = useStyles();
  const selectedOption = options.find((o) => o.value === value);

  const notifyChange = (nextValue) => {
    if (disabled) return;
    if (typeof onChange === 'function') onChange(nextValue);
  };

  return (
    <div className={classes.root}>
      <Typography className={classes.title}>Tipo autenticazione</Typography>

      <Grid container spacing={2}>
        {options.map((opt) => {
          const isSelected = opt.value === value;

          return (
            <Grid item xs={12} sm={6} key={opt.value}>
              <Paper
                elevation={0}
                className={[
                  classes.optionCard,
                  isSelected ? classes.optionCardSelected : '',
                  disabled ? classes.optionCardDisabled : '',
                ].join(' ')}
                role="button"
                tabIndex={disabled ? -1 : 0}
                onClick={() => notifyChange(opt.value)}
                onKeyDown={(e) => {
                  if (disabled) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    notifyChange(opt.value);
                  }
                }}
              >
                <div className={classes.optionHeaderRow}>
                  <Typography className={classes.optionTitle}>
                    {opt.title || opt.label}
                  </Typography>

                  {/* mini indicatore selezione */}
                  <div
                    className={[
                      classes.indicatorOuter,
                      isSelected ? classes.indicatorOuterSelected : '',
                    ].join(' ')}
                  >
                    {isSelected ? (
                      <div className={classes.indicatorInner} />
                    ) : null}
                  </div>
                </div>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {selectedOption?.helper ? (
        <Typography variant="caption" className={classes.helper}>
          {selectedOption.helper}
        </Typography>
      ) : null}
    </div>
  );
};

AuthTypeSelector.propTypes = {
  value: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      title: PropTypes.string,
      label: PropTypes.string,
      helper: PropTypes.string,
    }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default AuthTypeSelector;
